/**
 * CryptoCheckout Component (Updated with QR Code Support)
 * 
 * Now includes QR codes for easy mobile wallet scanning.
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { 
  X, Copy, CheckCircle2, Clock, AlertCircle, Loader2, 
  Wallet, ArrowRight, Shield, QrCode 
} from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import PaymentService, { PAYMENT_TYPES } from '../../services/paymentService';

/* ── Network icons ── */
const NETWORK_COLORS = {
  ethereum: { bg: '#627EEA', label: 'ETH' },
  bitcoin:  { bg: '#F7931A', label: 'BTC' },
  bsc:      { bg: '#F3BA2F', label: 'BSC' },
  tron:     { bg: '#FF0013', label: 'TRX' },
  polygon:  { bg: '#8247E5', label: 'MATIC' },
};

function NetworkBadge({ network, size = 32 }) {
  const info = NETWORK_COLORS[network] || { bg: '#888', label: '?' };
  return (
    <div className="rounded-lg flex items-center justify-center font-bold text-white text-xs"
      style={{ width: size, height: size, backgroundColor: info.bg }}>
      {info.label}
    </div>
  );
}

function CopyBtn({ text, label = 'Copy' }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <button onClick={copy} className="flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium transition-all"
      style={{
        backgroundColor: copied ? 'color-mix(in srgb, var(--accent-success) 15%, transparent)' : 'var(--bg-tertiary)',
        color: copied ? 'var(--accent-success)' : 'var(--text-secondary)',
      }}>
      {copied ? <CheckCircle2 className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
      {copied ? 'Copied!' : label}
    </button>
  );
}

function Countdown({ expiresAt }) {
  const [remaining, setRemaining] = useState('');
  useEffect(() => {
    const update = () => {
      const diff = new Date(expiresAt) - Date.now();
      if (diff <= 0) { setRemaining('Expired'); return; }
      const m = Math.floor(diff / 60000);
      const s = Math.floor((diff % 60000) / 1000);
      setRemaining(`${m}:${s.toString().padStart(2, '0')}`);
    };
    update();
    const interval = setInterval(update, 1000);
    return () => clearInterval(interval);
  }, [expiresAt]);
  const isLow = remaining && !remaining.includes('Expired') && parseInt(remaining) < 5;
  return (
    <span className="font-mono text-sm font-bold" style={{ color: isLow ? 'var(--accent-danger)' : 'var(--text-primary)' }}>
      {remaining}
    </span>
  );
}

/**
 * Universal CryptoCheckout Component
 * @param {Object} props
 * @param {string} props.type - Payment type (PAYMENT_TYPES constant)
 * @param {Object} props.item - Item being purchased (new prop)
 * @param {Object} props.plugin - Plugin being purchased (old prop - backward compatible)
 * @param {Function} props.onClose - Close modal callback
 * @param {Function} props.onSuccess - Success callback
 */
export default function CryptoCheckout({ 
  type = PAYMENT_TYPES.PLUGIN_PURCHASE, 
  item,
  plugin,  // Backward compatibility with old prop name
  onClose, 
  onSuccess 
}) {
  const [step, setStep] = useState('select_network');
  const [networks, setNetworks] = useState([]);
  const [selectedNetwork, setSelectedNetwork] = useState(null);
  const [selectedCurrency, setSelectedCurrency] = useState(null);
  const [payment, setPayment] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showQR, setShowQR] = useState(false);
  const pollControllerRef = useRef(null);

  // Backward compatibility: use plugin prop if item is not provided
  const actualItem = item || plugin;

  // Get item details based on type
  const getItemDetails = () => {
    if (!actualItem) {
      return {
        title: 'Make Payment',
        price: 0,
        cycle: 'one-time',
      };
    }

    switch (type) {
      case PAYMENT_TYPES.PLUGIN_PURCHASE:
        return {
          title: `Purchase ${actualItem.name || actualItem.label}`,
          price: parseFloat(actualItem.price || 0),
          cycle: actualItem.billing_cycle === 'one_time' ? 'one-time' : actualItem.billing_cycle || 'one-time',
        };
      case PAYMENT_TYPES.DEPOSIT:
        return {
          title: 'Deposit Funds',
          price: actualItem.amount || 0,
          cycle: 'one-time',
        };
      case PAYMENT_TYPES.SUBSCRIPTION:
        return {
          title: `Subscribe to ${actualItem.name}`,
          price: actualItem.price || 0,
          cycle: actualItem.billing_cycle || 'monthly',
        };
      default:
        return {
          title: 'Make Payment',
          price: actualItem.amount || actualItem.price || 0,
          cycle: 'one-time',
        };
    }
  };

  const itemDetails = getItemDetails();

  // Load payment options
  useEffect(() => {
    PaymentService.getPaymentOptions('amorpay')
      .then(data => {
        console.log('Payment options loaded:', data);
        setNetworks(data || []);
      })
      .catch(err => {
        console.error('Failed to load payment options:', err);
        setError('Failed to load payment options');
      });
  }, []);

  // Start polling payment status
  const startPolling = useCallback((paymentId) => {
    if (pollControllerRef.current) {
      pollControllerRef.current.stop();
    }

    console.log('Starting payment status polling for:', paymentId);

    pollControllerRef.current = PaymentService.pollPaymentStatus(
      paymentId,
      type,
      (status) => {
        console.log('Payment status update:', status);

        if (status.confirmed || status.status === 'confirmed') {
          setPayment(prev => ({ ...prev, ...status }));
          setStep('confirmed');
          if (pollControllerRef.current) {
            pollControllerRef.current.stop();
          }
        } else if (status.status === 'expired') {
          setError('Payment expired. Please try again.');
          setStep('select_network');
          if (pollControllerRef.current) {
            pollControllerRef.current.stop();
          }
        }
      },
      {
        initialInterval: 5000,
        maxInterval: 15000,
        maxAttempts: 60,
      }
    );
  }, [type]);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollControllerRef.current) {
        pollControllerRef.current.stop();
      }
    };
  }, []);

  // Handle purchase
  const handlePurchase = async () => {
    if (!selectedNetwork || !selectedCurrency) return;
    
    if (!actualItem) {
      setError('No item selected for purchase');
      return;
    }
    
    setLoading(true);
    setError(null);

    try {
      let paymentData;

      // Create payment based on type
      if (type === PAYMENT_TYPES.PLUGIN_PURCHASE) {
        paymentData = await PaymentService.createPluginPayment(
          actualItem.plugin_key || actualItem.pluginKey,
          selectedCurrency,
          selectedNetwork
        );
      } else if (type === PAYMENT_TYPES.DEPOSIT) {
        paymentData = await PaymentService.createDeposit(
          actualItem.account_id,
          actualItem.amount,
          selectedCurrency,
          selectedNetwork
        );
      } else if (type === PAYMENT_TYPES.SUBSCRIPTION) {
        paymentData = await PaymentService.createSubscription(
          actualItem.plan_id,
          selectedCurrency,
          selectedNetwork
        );
      } else {
        // Generic payment creation
        paymentData = await PaymentService.createPayment({
          type,
          currency: selectedCurrency,
          network: selectedNetwork,
          amount: actualItem.amount,
          metadata: actualItem,
        });
      }

      console.log('Payment created:', paymentData);

      // Handle free/immediate activation
      if (paymentData.free || paymentData.activated) {
        setStep('confirmed');
        return;
      }

      setPayment(paymentData);
      setStep('awaiting_payment');
      startPolling(paymentData.payment_id || paymentData.gateway_payment_id);

    } catch (err) {
      console.error('Payment creation failed:', err);
      const msg = err.response?.data?.error || err.message || 'Payment creation failed';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4"
      style={{ backgroundColor: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)' }}
      onClick={onClose}>
      <div className="w-full max-w-md rounded-2xl border overflow-hidden"
        style={{ backgroundColor: 'var(--bg-primary)', borderColor: 'var(--border-color)' }}
        onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b" style={{ borderColor: 'var(--border-color)' }}>
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center"
              style={{ backgroundColor: 'color-mix(in srgb, var(--accent-primary) 15%, transparent)' }}>
              <Wallet className="w-4 h-4" style={{ color: 'var(--accent-primary)' }} />
            </div>
            <div>
              <h3 className="text-sm font-bold" style={{ color: 'var(--text-primary)' }}>
                {step === 'confirmed' ? 'Payment Confirmed!' : itemDetails.title}
              </h3>
              <p className="text-[11px]" style={{ color: 'var(--text-tertiary)' }}>
                ${itemDetails.price.toFixed(2)} / {itemDetails.cycle}
              </p>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:opacity-70">
            <X className="w-4 h-4" style={{ color: 'var(--text-tertiary)' }} />
          </button>
        </div>

        <div className="p-5 space-y-4">

          {error && (
            <div className="flex items-center gap-2 rounded-xl px-3 py-2.5 text-xs border"
              style={{
                backgroundColor: 'color-mix(in srgb, var(--accent-danger) 8%, transparent)',
                borderColor: 'color-mix(in srgb, var(--accent-danger) 20%, transparent)',
                color: 'var(--accent-danger)',
              }}>
              <AlertCircle className="w-4 h-4 shrink-0" />
              <span className="flex-1">{error}</span>
              <button onClick={() => setError(null)}><X className="w-3.5 h-3.5" /></button>
            </div>
          )}

          {/* STEP 1: Select Network */}
          {step === 'select_network' && (
            <>
              <p className="text-xs font-medium" style={{ color: 'var(--text-secondary)' }}>Select payment method</p>
              <div className="space-y-2">
                {networks.map(net => (
                  <button key={net.network} 
                    onClick={() => { 
                      setSelectedNetwork(net.network); 
                      setSelectedCurrency(net.supported_currencies?.[0] || 'ETH'); 
                    }}
                    className="w-full flex items-center gap-3 p-3 rounded-xl border transition-all text-left"
                    style={{
                      backgroundColor: selectedNetwork === net.network ? 'color-mix(in srgb, var(--accent-primary) 8%, transparent)' : 'var(--bg-secondary)',
                      borderColor: selectedNetwork === net.network ? 'var(--accent-primary)' : 'var(--border-color)',
                    }}>
                    <NetworkBadge network={net.network} />
                    <div className="flex-1">
                      <p className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>
                        {net.network_name || net.name}
                      </p>
                      <p className="text-[11px]" style={{ color: 'var(--text-tertiary)' }}>
                        Pay with {(net.supported_currencies || []).join(', ')}
                      </p>
                    </div>
                    {selectedNetwork === net.network && (
                      <CheckCircle2 className="w-5 h-5" style={{ color: 'var(--accent-primary)' }} />
                    )}
                  </button>
                ))}
              </div>
              
              {networks.length === 0 && !error && (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin" style={{ color: 'var(--accent-primary)' }} />
                </div>
              )}
              
              <button onClick={handlePurchase} disabled={!selectedNetwork || loading}
                className="w-full flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-bold text-white transition-all disabled:opacity-40"
                style={{ backgroundColor: 'var(--accent-primary)' }}>
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
                {loading ? 'Creating payment...' : 'Continue'}
              </button>
            </>
          )}

          {/* STEP 2: Awaiting Payment */}
          {step === 'awaiting_payment' && payment && (
            <>
              <div className="rounded-xl border p-4 text-center" style={{ backgroundColor: 'var(--bg-secondary)', borderColor: 'var(--border-color)' }}>
                <p className="text-[11px] font-medium mb-1" style={{ color: 'var(--text-tertiary)' }}>Send exactly</p>
                <div className="flex items-center justify-center gap-2">
                  <p className="text-2xl font-black font-mono" style={{ color: 'var(--text-primary)' }}>
                    {payment.amount}
                  </p>
                  <span className="text-sm font-bold" style={{ color: 'var(--accent-primary)' }}>
                    {payment.currency}
                  </span>
                </div>
                {payment.fiatAmount && (
                  <p className="text-[11px] mt-1" style={{ color: 'var(--text-tertiary)' }}>
                    ≈ ${payment.fiatAmount.toFixed(2)} USD
                  </p>
                )}
              </div>

              {/* QR Code Section */}
              <div className="flex items-center justify-center">
                <button 
                  onClick={() => setShowQR(!showQR)}
                  className="flex items-center gap-2 px-4 py-2 rounded-xl border transition-all"
                  style={{
                    backgroundColor: showQR ? 'color-mix(in srgb, var(--accent-primary) 10%, transparent)' : 'var(--bg-secondary)',
                    borderColor: showQR ? 'var(--accent-primary)' : 'var(--border-color)',
                    color: showQR ? 'var(--accent-primary)' : 'var(--text-secondary)',
                  }}>
                  <QrCode className="w-4 h-4" />
                  <span className="text-xs font-medium">{showQR ? 'Hide' : 'Show'} QR Code</span>
                </button>
              </div>

              {/* QR Code Display */}
              {showQR && (
                <div className="flex flex-col items-center gap-3 p-4 rounded-xl border" style={{
                  backgroundColor: 'var(--bg-secondary)',
                  borderColor: 'var(--border-color)',
                }}>
                  <div className="p-3 rounded-xl bg-white">
                    <QRCodeSVG 
                      value={payment.payment_address || payment.walletAddress}
                      size={200}
                      level="H"
                      includeMargin={false}
                    />
                  </div>
                  <p className="text-[10px] text-center" style={{ color: 'var(--text-tertiary)' }}>
                    Scan with your mobile wallet
                  </p>
                </div>
              )}

              {/* Address Display */}
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <p className="text-[11px] font-medium" style={{ color: 'var(--text-tertiary)' }}>
                    To this {payment.network} address
                  </p>
                  <CopyBtn text={payment.payment_address || payment.walletAddress} />
                </div>
                <div className="p-3 rounded-xl border font-mono text-xs break-all" style={{
                  backgroundColor: 'var(--bg-secondary)', 
                  borderColor: 'var(--border-color)', 
                  color: 'var(--text-primary)',
                }}>
                  {payment.payment_address || payment.walletAddress}
                </div>
              </div>

              {payment.expires_at && (
                <div className="flex items-center justify-between p-3 rounded-xl border" style={{
                  backgroundColor: 'color-mix(in srgb, var(--accent-warning) 6%, transparent)',
                  borderColor: 'color-mix(in srgb, var(--accent-warning) 20%, transparent)',
                }}>
                  <div className="flex items-center gap-2">
                    <Clock className="w-4 h-4" style={{ color: 'var(--accent-warning)' }} />
                    <span className="text-xs font-medium" style={{ color: 'var(--accent-warning)' }}>
                      Expires in
                    </span>
                  </div>
                  <Countdown expiresAt={payment.expires_at} />
                </div>
              )}

              <div className="flex items-center gap-3 p-3 rounded-xl" style={{ backgroundColor: 'var(--bg-secondary)' }}>
                <Loader2 className="w-5 h-5 animate-spin" style={{ color: 'var(--accent-primary)' }} />
                <div>
                  <p className="text-xs font-semibold" style={{ color: 'var(--text-primary)' }}>
                    Waiting for payment...
                  </p>
                  <p className="text-[11px]" style={{ color: 'var(--text-tertiary)' }}>
                    Send the exact amount. We'll detect it automatically.
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-2 text-[11px]" style={{ color: 'var(--text-tertiary)' }}>
                <Shield className="w-3.5 h-3.5" />
                <span>Verified on-chain by AmorPay. No intermediaries.</span>
              </div>
            </>
          )}

          {/* STEP 3: Confirmed */}
          {step === 'confirmed' && (
            <div className="text-center py-4 space-y-4">
              <div className="w-16 h-16 mx-auto rounded-full flex items-center justify-center"
                style={{ backgroundColor: 'color-mix(in srgb, var(--accent-success) 15%, transparent)' }}>
                <CheckCircle2 className="w-8 h-8" style={{ color: 'var(--accent-success)' }} />
              </div>
              <div>
                <h3 className="text-lg font-bold" style={{ color: 'var(--text-primary)' }}>
                  Payment Confirmed!
                </h3>
                <p className="text-sm mt-1" style={{ color: 'var(--text-secondary)' }}>
                  {type === PAYMENT_TYPES.PLUGIN_PURCHASE && actualItem && (
                    <><strong>{actualItem.name || actualItem.label}</strong> has been activated.</>
                  )}
                  {type === PAYMENT_TYPES.DEPOSIT && (
                    <>Your deposit has been credited to your account.</>
                  )}
                  {type === PAYMENT_TYPES.SUBSCRIPTION && actualItem && (
                    <>Your subscription is now active.</>
                  )}
                  {!actualItem && <>Your payment has been confirmed.</>}
                </p>
              </div>
              {payment?.tx_hash && (
                <p className="text-[11px] font-mono" style={{ color: 'var(--text-tertiary)' }}>
                  TX: {payment.tx_hash.substring(0, 20)}...
                </p>
              )}
              <button onClick={() => { onSuccess?.(); onClose(); }}
                className="w-full py-3 rounded-xl text-sm font-bold text-white"
                style={{ backgroundColor: 'var(--accent-success)' }}>
                Done
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}